import Phaser from 'phaser';
import { PLAYER, BOUNDS } from '../config/constants';
import { Toast } from './Toast';

export class ToasterTruck extends Phaser.Physics.Arcade.Sprite {
  private keyLeft!: Phaser.Input.Keyboard.Key;
  private keyRight!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keySpace!: Phaser.Input.Keyboard.Key;
  private lastFired: number = 0;
  private toastGroup: Phaser.Physics.Arcade.Group;
  private leftWheelGlow!: Phaser.GameObjects.Sprite;
  private rightWheelGlow!: Phaser.GameObjects.Sprite;
  private glowTime: number = 0;
  
  // Power-up states
  private rapidFire: boolean = false;
  private spreadShot: boolean = false;
  private shieldActive: boolean = false;
  private shieldSprite!: Phaser.GameObjects.Sprite;

  constructor(scene: Phaser.Scene, x: number, y: number, toastGroup: Phaser.Physics.Arcade.Group) {
    super(scene, x, y, 'truck');
    
    this.toastGroup = toastGroup;
    
    // Add to scene and enable physics
    scene.add.existing(this);
    scene.physics.add.existing(this);
    
    // Set up physics body
    this.setCollideWorldBounds(true);
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setSize(PLAYER.WIDTH - 10, PLAYER.HEIGHT - 10);
    
    // Set up ALL input keys once in constructor
    const keyboard = scene.input.keyboard;
    if (keyboard) {
      this.keyLeft = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
      this.keyRight = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT);
      this.keyA = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.A);
      this.keyD = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.D);
      this.keySpace = keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    }

    // Create wheel glow effects
    this.createWheelGlow();
    
    // Create shield sprite (initially invisible)
    this.createShieldVisual();
  }

  private createWheelGlow(): void {
    // Left wheel glow (positioned relative to truck - front wheel)
    this.leftWheelGlow = this.scene.add.sprite(this.x - 20, this.y + 22, 'wheelGlow');
    this.leftWheelGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.leftWheelGlow.setAlpha(0.7);
    this.leftWheelGlow.setScale(1.2);
    
    // Right wheel glow (rear wheel)
    this.rightWheelGlow = this.scene.add.sprite(this.x + 20, this.y + 22, 'wheelGlow');
    this.rightWheelGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.rightWheelGlow.setAlpha(0.7);
    this.rightWheelGlow.setScale(1.2);
  }

  update(time: number, _delta: number): void {
    // Check for left/right movement (arrows or A/D)
    const leftDown = this.keyLeft?.isDown || this.keyA?.isDown;
    const rightDown = this.keyRight?.isDown || this.keyD?.isDown;
    const spaceDown = this.keySpace?.isDown;
    
    if (leftDown) {
      this.setVelocityX(-PLAYER.SPEED);
    } else if (rightDown) {
      this.setVelocityX(PLAYER.SPEED);
    } else {
      this.setVelocityX(0);
    }

    // Clamp position to bounds
    this.x = Phaser.Math.Clamp(this.x, BOUNDS.LEFT, BOUNDS.RIGHT);

    // Handle firing - rapid fire halves the fire rate
    const fireRate = this.rapidFire ? PLAYER.FIRE_RATE / 2.5 : PLAYER.FIRE_RATE;
    if (spaceDown && time > this.lastFired + fireRate) {
      this.fire(time);
    }
    
    // Update shield position
    this.updateShieldVisual();

    // Update wheel glow positions and animation
    this.updateWheelGlow(time);
  }

  private updateWheelGlow(_time: number): void {
    // Update positions to follow truck (front and rear wheels)
    this.leftWheelGlow.setPosition(this.x - 20, this.y + 22);
    this.rightWheelGlow.setPosition(this.x + 20, this.y + 22);
    
    // Animate glow intensity with sine wave
    this.glowTime += 0.05;
    const intensity = 0.6 + 0.3 * Math.sin(this.glowTime * 3);
    const scale = 1.0 + 0.3 * Math.sin(this.glowTime * 3);
    
    this.leftWheelGlow.setAlpha(intensity);
    this.rightWheelGlow.setAlpha(intensity);
    this.leftWheelGlow.setScale(scale);
    this.rightWheelGlow.setScale(scale);
  }

  private fire(time: number): void {
    this.lastFired = time;

    if (this.spreadShot) {
      // Spread shot: fire 3 toasts in a spread pattern
      // Center toast
      new Toast(this.scene, this.x + 9, this.y - 30, this.toastGroup);
      
      // Left angled toast
      const leftToast = new Toast(this.scene, this.x - 5, this.y - 28, this.toastGroup);
      leftToast.setVelocity(-60, -PLAYER.TOAST_SPEED);
      leftToast.setAngle(-15);
      
      // Right angled toast
      const rightToast = new Toast(this.scene, this.x + 23, this.y - 28, this.toastGroup);
      rightToast.setVelocity(60, -PLAYER.TOAST_SPEED);
      rightToast.setAngle(15);
    } else {
      // Normal fire: toast from both slots
      new Toast(
        this.scene,
        this.x - 1,
        this.y - 30,
        this.toastGroup
      );
      
      new Toast(
        this.scene,
        this.x + 19,
        this.y - 28,
        this.toastGroup
      );
    }

    // Firing animation - slight bounce/recoil
    this.scene.tweens.add({
      targets: this,
      y: this.y + 4,
      duration: 50,
      yoyo: true,
      ease: 'Power2',
    });

    // Flash the wheel glows brighter when firing
    this.scene.tweens.add({
      targets: [this.leftWheelGlow, this.rightWheelGlow],
      alpha: 1,
      scale: 1.5,
      duration: 50,
      yoyo: true,
    });
  }

  private createShieldVisual(): void {
    this.shieldSprite = this.scene.add.sprite(this.x, this.y, 'shieldEffect');
    this.shieldSprite.setBlendMode(Phaser.BlendModes.ADD);
    this.shieldSprite.setAlpha(0);
    this.shieldSprite.setDepth(1);
  }

  private updateShieldVisual(): void {
    if (this.shieldSprite) {
      this.shieldSprite.setPosition(this.x, this.y);
      
      if (this.shieldActive) {
        // Pulsing shield effect
        const pulse = 0.4 + 0.2 * Math.sin(this.glowTime * 4);
        this.shieldSprite.setAlpha(pulse);
        this.shieldSprite.setScale(1 + 0.05 * Math.sin(this.glowTime * 6));
      } else {
        this.shieldSprite.setAlpha(0);
      }
    }
  }

  // Power-up setters
  setRapidFire(active: boolean): void {
    this.rapidFire = active;
    if (active) {
      this.setTint(0xff8844);
    } else if (!this.shieldActive && !this.spreadShot) {
      this.clearTint();
    }
  }

  setSpreadShot(active: boolean): void {
    this.spreadShot = active;
    if (active) {
      this.setTint(0xff4488);
    } else if (!this.shieldActive && !this.rapidFire) {
      this.clearTint();
    }
  }

  setShieldActive(active: boolean): void {
    this.shieldActive = active;
    if (active) {
      // Flash effect when shield activates
      this.scene.tweens.add({
        targets: this.shieldSprite,
        alpha: 0.8,
        duration: 200,
        yoyo: true,
        repeat: 2,
      });
    }
  }

  hasShield(): boolean {
    return this.shieldActive;
  }

  destroy(fromScene?: boolean): void {
    this.leftWheelGlow?.destroy();
    this.rightWheelGlow?.destroy();
    this.shieldSprite?.destroy();
    super.destroy(fromScene);
  }
}

